import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './common/Auth';
import Footer from './common/Footer';
import logo from './simulcastlogo.svg';
import {
	BookOpen,
	LayoutGrid,
	Palette,
	Calendar,
	Users,
	Router,
	Layers,
	Radio,
	Clock,
	Sparkles,
} from 'lucide-react';

const sections = [
	{ id: 'overview', label: 'Overview' },
	{ id: 'how-it-works', label: 'How It Works' },
	{ id: 'getting-started', label: 'Getting Started' },
	{ id: 'systems', label: 'Systems' },
	{ id: 'zones', label: 'Zones' },
	{ id: 'scenes', label: 'Scenes' },
	{ id: 'schedules', label: 'Schedules' },
	{ id: 'jules', label: 'Jules AI' },
	{ id: 'repeating-rules', label: 'Repeating Rules' },
	{ id: 'gateways', label: 'Gateways' },
	{ id: 'users', label: 'Users & Permissions' },
	{ id: 'live-control', label: 'Live Control' },
];

function Section({ id, icon: Icon, title, children }) {
	return (
		<section id={id} className="scroll-mt-24">
			<h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2.5">
				{Icon && <Icon size={22} className="text-[#880e4f] shrink-0" />}
				{title}
			</h2>
			<div className="space-y-4 text-gray-700 leading-relaxed text-[15px]">{children}</div>
		</section>
	);
}

function Subheading({ children }) {
	return <h3 className="text-base font-semibold text-gray-900 mt-6 mb-2">{children}</h3>;
}

function StepList({ items }) {
	return (
		<ol className="list-decimal list-outside ml-5 space-y-2 marker:text-[#880e4f] marker:font-semibold">
			{items.map((item, i) => (
				<li key={i}>{item}</li>
			))}
		</ol>
	);
}

function ExampleBox({ title, children }) {
	return (
		<div className="bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-sm">
			{title && <div className="font-semibold text-gray-900 mb-1.5">{title}</div>}
			<div className="text-gray-700 space-y-1">{children}</div>
		</div>
	);
}

const Docs = () => {
	const currentUser = useAuth();

	return (
		<div className="min-h-screen bg-gray-50 text-gray-900">
			<header className="sticky top-0 z-30 bg-[#880e4f] text-white border-b border-[#6b0b3f] shadow-sm">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
					<Link to="/" className="shrink-0">
						<img src={logo} alt="Simulcast" className="h-7 w-auto brightness-0 invert" />
					</Link>
					<div className="flex-1 min-w-0">
						<div className="font-semibold text-sm sm:text-base truncate">Simulcast Documentation</div>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<Link
							to="/"
							className="hidden sm:inline-block px-3 py-1.5 text-xs rounded-lg hover:bg-white/15 transition-colors"
						>
							Home
						</Link>
						{currentUser ? (
							<a
								href="/app"
								className="px-3 py-1.5 text-xs rounded-lg bg-white/15 hover:bg-white/25 transition-colors font-medium"
							>
								Open App
							</a>
						) : (
							<a
								href="/login"
								className="px-3 py-1.5 text-xs rounded-lg bg-white/15 hover:bg-white/25 transition-colors font-medium"
							>
								Log in
							</a>
						)}
					</div>
				</div>
			</header>

			<div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
				<div className="mb-10 sm:mb-12">
					<div className="inline-flex items-center gap-2 text-sm text-[#880e4f] font-medium mb-3">
						<BookOpen size={16} />
						User Guide
					</div>
					<h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-3">
						Simulcast User Documentation
					</h1>
					<p className="text-lg text-gray-600 max-w-3xl">
						<strong>Lighting control at scale.</strong> Learn how Simulcast synchronizes looks across
						a single building, an entire city, or installations nationwide — with powerful scheduling,
						multi-gateway systems, and Jules AI assistance.
					</p>
				</div>

				<div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
					<nav className="lg:w-56 shrink-0">
						<div className="lg:sticky lg:top-24 bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
							<div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
								On this page
							</div>
							<ul className="space-y-1 text-sm">
								{sections.map(({ id, label }) => (
									<li key={id}>
										<a
											href={`#${id}`}
											className="block px-2.5 py-1.5 rounded-lg text-gray-600 hover:text-[#880e4f] hover:bg-[#880e4f]/5 transition-colors"
										>
											{label}
										</a>
									</li>
								))}
							</ul>
						</div>
					</nav>

					<main className="flex-1 min-w-0 space-y-12 sm:space-y-14">
						<Section id="overview" icon={BookOpen} title="Overview">
							<p>
								<strong>Simulcast</strong> is <em>lighting control at scale</em> — a professional
								platform from Phospec Industries that lets you design, schedule, and synchronize
								colorful lighting from a web browser. Whether you operate a single venue or coordinate
								looks across a downtown district, a metro area, or sites spread across the country,
								Simulcast keeps every zone on the same scene at the same moment.
							</p>
							<p>
								At the heart of Simulcast is <strong>scheduling</strong>: fixed times, repeating
								patterns, and astronomical triggers (sunrise, sunset, and more) that run automatically
								without an on-site technician. Combine that with <strong>multiple gateways</strong>{' '}
								per system — each outputting sACN/DMX on its own universe — and you can grow one
								logical installation from a few fixtures to thousands, all behaving as a single,
								cohesive show.
							</p>
							<p>
								Simulcast is built for installations where reliability, remote management, and
								long-term simplicity matter: churches, venues, architectural lighting, sports
								facilities, civic displays, and any site using sACN (Streaming ACN) or DMX.
								It can command lighting directly or integrate with existing control systems, putting
								communicating with color in the hands of marketing, management, and facility teams.
							</p>
							<Subheading>Scale and synchronization</Subheading>
							<p>
								Each <strong>system</strong> is a self-contained control domain — zones, scenes,
								schedules, and gateways for one site or campaign. Operate many systems from one
								account: a church and its satellite campuses, every building on a streetscape, or
								properties in different time zones. Schedules respect each system&apos;s local
								timezone and coordinates, so sunset in Denver and sunset in Boston both fire at the
								right moment for their location.
							</p>
							<p>
								Within a system, <strong>all gateways receive the same scene output</strong> when a
								schedule fires or you activate a look manually. Add gateways as the installation
								grows — new wings, parking structures, bridge facades, or distant façades on separate
								networks — without splitting your creative work across disconnected controllers.
							</p>
							<Subheading>Key concepts</Subheading>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>System</strong> — a logical grouping of zones, scenes, schedules, and
									gateways for one installation or coordinated site. You can run many systems from
									one account.
								</li>
								<li>
									<strong>Zone</strong> — a controllable color output channel (for example, a section
									of LED tape or a fixture group).
								</li>
								<li>
									<strong>Scene</strong> — a saved look: either a static color per zone or an animated
									cycle that transitions between colors over time.
								</li>
								<li>
									<strong>Schedule</strong> — a rule that activates a scene at a fixed time, on a
									repeating pattern, or at an astronomical event such as sunset. Scheduling is the
									primary way large installations stay synchronized day after day.
								</li>
								<li>
									<strong>Gateway</strong> — a hardware device that receives commands from Simulcast
									and outputs sACN/DMX. Multiple gateways per system let you cover any physical size.
								</li>
								<li>
									<strong>Jules</strong> — Simulcast&apos;s AI lighting assistant (available on
									eligible plans). Jules suggests event-themed colors, helps name scenes and
									schedules, and can optionally apply a daily look automatically.
								</li>
							</ul>
						</Section>

						<Section id="how-it-works" icon={Radio} title="How It Works">
							<p>
								Simulcast runs as a cloud-connected web application. When you make changes in the
								browser — activate a scene, edit a schedule, or update gateway settings — those
								changes are saved and pushed to every gateway in the system over the internet. That
								same pipeline works whether you have one gateway in a single room or dozens spread
								across a campus, a city, or multiple states.
							</p>
							<Subheading>The control flow</Subheading>
							<StepList
								items={[
									<>
										You configure a <strong>system</strong> with zones, scenes, and schedules in
										the Simulcast web app. Create additional systems for each distinct site or
										region you need to manage.
									</>,
									<>
										You register one or more <strong>gateways</strong> and link them to the system.
										Each gateway is assigned an sACN universe. Add gateways as the footprint grows —
										they all share the same scenes and schedules.
									</>,
									<>
										When you activate a scene (manually or via a schedule), Simulcast calculates
										the RGB values for each zone once, then distributes that output to every
										gateway simultaneously.
									</>,
									<>
										Each gateway outputs sACN on its configured universe to drive local fixtures.
										The result is one cohesive look across the entire system, regardless of physical
										distance.
									</>,
								]}
							/>
							<Subheading>Scheduling at scale</Subheading>
							<p>
								<strong>Schedules</strong> are how Simulcast keeps large installations synchronized
								without daily manual intervention. A schedule can fire at a specific date and time,
								repeat on a custom pattern (daily, weekly, and more via recurrence rules), or trigger
								relative to sunrise or sunset. Astronomical scheduling uses each system&apos;s latitude,
								longitude, and timezone — so a nationwide portfolio of systems each follows the correct
								local sun times automatically.
							</p>
							<p>
								Repeating rules let you define complex patterns: weekdays only, seasonal date ranges,
								every few hours, or sunset every day year-round. See{' '}
								<a href="#schedules" className="text-[#880e4f] hover:underline">Schedules</a> and{' '}
								<a href="#repeating-rules" className="text-[#880e4f] hover:underline">
									Repeating Rules
								</a>{' '}
								for full detail.
							</p>
							<Subheading>Multi-user access</Subheading>
							<p>
								System owners can invite guests with granular permissions — for example, allowing
								someone to control scenes without editing schedules, or to view without making
								changes. Guest users see shared systems under <strong>Guest Access</strong> on the
								Systems page.
							</p>
						</Section>

						<Section id="getting-started" icon={LayoutGrid} title="Getting Started">
							<StepList
								items={[
									<>
										<strong>Create an account</strong> from the Simulcast landing page, or log in if
										you already have one.
									</>,
									<>
										From the <strong>Systems</strong> view, click <strong>New System</strong> and
										give your installation a name, location, timezone, and coordinates (coordinates
										are optional but required for astronomical schedules).
									</>,
									<>
										Select the system to open its workspace. Use the sidebar to navigate between{' '}
										<strong>Scenes</strong>, <strong>Schedule</strong>, <strong>Users</strong>, and{' '}
										<strong>Setup</strong>.
									</>,
									<>
										In <strong>Setup</strong>, define your zones and add gateways before creating
										scenes.
									</>,
									<>
										Create scenes, build schedules, and activate a scene to verify output on your
										hardware.
									</>,
								]}
							/>
							<p className="text-sm text-gray-500 bg-gray-100 rounded-xl px-4 py-3 border border-gray-200">
								Tip: configure zones first, then gateways, then scenes. Schedules reference scenes, so
								scenes must exist before you can schedule them.
							</p>
						</Section>

						<Section id="systems" icon={LayoutGrid} title="Systems">
							<p>
								A system represents one controllable installation — a building, a campus, a
								streetscape, or any coordinated group of fixtures that should run the same scenes and
								schedules together. Each system has its own zones, scenes, schedules, gateways, and
								user permissions.
							</p>
							<p>
								Your account can own or access <strong>many systems</strong>. That is how Simulcast
								scales from a single room to a city or a national portfolio: one system per site (or
								per logical grouping), each with its own timezone and coordinates, all managed from
								the same browser session. A schedule that runs at sunset in one system does not affect
								another — unless you configure the same scene and schedule in each, giving you
								consistent branding from coast to coast.
							</p>
							<Subheading>Creating and editing a system</Subheading>
							<p>
								On the Systems page, click <strong>New System</strong> or edit an existing system
								card. Configure:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Name</strong> — a descriptive label (for example, &ldquo;Sanctuary&rdquo;
									or &ldquo;North Parking Lot&rdquo;).
								</li>
								<li>
									<strong>Location</strong> — a human-readable address or description.
								</li>
								<li>
									<strong>Timezone</strong> — used for all schedule times and astronomical
									calculations.
								</li>
								<li>
									<strong>Latitude / Longitude</strong> — required for sunrise and sunset
									schedules. The system editor shows a preview of today&apos;s sun times when
									coordinates are set.
								</li>
							</ul>
							<Subheading>Guest systems</Subheading>
							<p>
								Systems shared with you appear under <strong>Guest Access</strong>. Your permissions
								on a guest system determine which actions you can take (control scenes, edit content,
								invite others, or delete).
							</p>
						</Section>

						<Section id="zones" icon={Layers} title="Zones">
							<p>
								Zones define the color output channels for a system. Most systems start with four
								zones, but you can add, edit, or remove zones in the <strong>Setup</strong> view.
							</p>
							<Subheading>Zone properties</Subheading>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Name</strong> — displayed throughout the app (for example, &ldquo;Altar&rdquo;
									or &ldquo;Exterior Soffit&rdquo;).
								</li>
								<li>
									<strong>Description</strong> — optional notes about the physical installation.
								</li>
								<li>
									<strong>Type</strong> — the fixture color model: RGB, RGBW, RGBA, RGBAW, RGBAWU, or
									RGBWAU. Choose the type that matches your hardware.
								</li>
							</ul>
							<p>
								When you change the number of zones, Simulcast adjusts the live output array to match.
								Existing scenes are tied to zone indices, so plan zone layout before building many
								scenes.
							</p>
						</Section>

						<Section id="scenes" icon={Palette} title="Scenes">
							<p>
								Scenes are the looks you activate on your installation. Open the <strong>Scenes</strong>{' '}
								view after selecting a system.
							</p>
							<Subheading>Scene types</Subheading>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Static scene</strong> — sets a fixed color for each zone. Use{' '}
									<strong>Add Scene</strong> to create one.
								</li>
								<li>
									<strong>Cycle</strong> — animates through a sequence of colors over time. Use{' '}
									<strong>Add Cycle</strong> to create one.
								</li>
								<li>
									<strong>Blackout</strong> — sets all zones to off (zero output). Click the blackout
									card to activate it immediately.
								</li>
							</ul>
							<Subheading>Activating scenes</Subheading>
							<p>
								Click any scene card to make it the active look. The system&apos;s current output
								updates and is pushed to connected gateways. The active scene is highlighted on the
								card grid.
							</p>
							<Subheading>Editing scenes</Subheading>
							<p>
								Open a scene to set colors per zone using the color picker. For cycles, define the
								sequence of color steps and transition timing. Scene names appear in schedules and
								on the calendar.
							</p>
						</Section>

						<Section id="schedules" icon={Calendar} title="Schedules">
							<p>
								<strong>Schedules</strong> are the backbone of Simulcast at scale — they automate scene
								changes so every gateway in a system switches together, on time, every day. Open the{' '}
								<strong>Schedule</strong> view after selecting a system to create, edit, and review
								schedules alongside a calendar that shows when events run.
							</p>

							<Subheading>Opening the schedule editor</Subheading>
							<p>You can create or edit a schedule in two ways:</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									Click <strong>Add</strong> on the Schedule page to start a new schedule with
									today&apos;s date pre-filled.
								</li>
								<li>
									Click a date on the calendar to open the editor with that date already selected.
								</li>
								<li>
									Click the edit icon on an existing schedule card in the list below the calendar.
								</li>
							</ul>

							<Subheading>Schedule fields</Subheading>
							<p>Every schedule has these core settings:</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Name</strong> — a descriptive label shown on schedule cards and the
									calendar (for example, &ldquo;Weekday Evening&rdquo; or &ldquo;Easter Service&rdquo;).
								</li>
								<li>
									<strong>Time type</strong> — <strong>Fixed</strong> (clock time) or{' '}
									<strong>Astronomical</strong> (sun-based). See below.
								</li>
								<li>
									<strong>Repeat</strong> — <strong>One-time</strong> (runs once) or{' '}
									<strong>Repeating</strong> (uses a repeat rule). See{' '}
									<a href="#repeating-rules" className="text-[#880e4f] hover:underline">
										Repeating Rules
									</a>{' '}
									for full detail.
								</li>
								<li>
									<strong>Scene</strong> — the scene or cycle to activate when the schedule fires.
									Choose <strong>None (blackout)</strong> to turn all zones off.
								</li>
							</ul>

							<Subheading>One-time schedules</Subheading>
							<p>
								With <strong>Repeat</strong> set to <strong>One-time</strong>, the schedule fires once
								and then completes. For fixed-time schedules, set the exact date and time. For
								astronomical schedules, set the start date — the schedule runs on that date at the
								calculated sun time.
							</p>
							<p>
								One-time schedules are ideal for special events, holidays, or one-off tests before
								turning a look into a repeating rule.
							</p>

							<Subheading>Fixed time</Subheading>
							<p>
								Fixed schedules use the date and time fields in the editor. Times are interpreted in
								the <strong>system timezone</strong> (set in system settings), not your browser&apos;s
								local timezone — so a schedule created for 6:00 PM always means 6:00 PM at the
								installation site.
							</p>
							<p>
								The calendar and schedule cards display the stored date and time. Repeating fixed
								schedules keep the same time of day on each occurrence; only the calendar date
								advances according to the repeat rule.
							</p>

							<Subheading>Astronomical time</Subheading>
							<p>
								Astronomical schedules trigger relative to sun events at the system&apos;s latitude
								and longitude. Before using this mode, set coordinates in system settings — the editor
								will warn you if they are missing.
							</p>
							<p>Available astronomical events include:</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5 columns-1 sm:columns-2 gap-x-8">
								<li>Sunrise</li>
								<li>Sunrise end</li>
								<li>Golden hour (morning)</li>
								<li>Solar noon</li>
								<li>Golden hour (evening)</li>
								<li>Sunset start</li>
								<li>Sunset</li>
								<li>Civil dawn / Civil dusk</li>
								<li>Nautical dawn / Nautical dusk</li>
								<li>Astronomical dawn / Astronomical dusk</li>
							</ul>
							<p>
								Use the <strong>Offset (minutes)</strong> field to run before or after the event.
								Negative values run earlier (for example, <strong>−30</strong> means 30 minutes
								before sunset). Positive values run later. The editor shows a{' '}
								<strong>Resolved</strong> preview with the actual clock time for the selected date.
							</p>
							<p>
								For repeating astronomical schedules, the clock time shifts throughout the year as
								sunrise and sunset change — you define the event and offset once; Simulcast recalculates
								the time for each occurrence.
							</p>

							<ExampleBox title="Example: exterior lights at dusk">
								<p>
									Event: <strong>Sunset</strong>, Offset: <strong>−15</strong>, Repeat:{' '}
									<strong>Repeating</strong>, every <strong>1 Day</strong>, scene:{' '}
									<strong>Exterior Warm White</strong>.
								</p>
								<p>Exterior zones turn on 15 minutes before sunset every day, year-round.</p>
							</ExampleBox>

							<Subheading>Linking scenes and blackout</Subheading>
							<p>
								The <strong>Scene</strong> dropdown lists all scenes and cycles for the current
								system. Cycles are marked with <strong>(cycle)</strong>. When a cycle is scheduled,
								Simulcast starts the cycle animation when the schedule fires.
							</p>
							<p>
								Select <strong>None (blackout)</strong> to set all zones to zero output at the
								scheduled time — useful for a nightly &ldquo;all off&rdquo; rule separate from your
								daytime looks.
							</p>

							<Subheading>The calendar</Subheading>
							<p>
								The Schedule page calendar highlights days when schedules run. Click a day to inspect
								or add events. Schedule cards below the calendar list every schedule with:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>The schedule name and linked scene preview colors</li>
								<li>Fixed date/time, or astronomical event label plus resolved clock time</li>
								<li>A <strong>One-time</strong> label, or a plain-language repeat summary</li>
							</ul>
							<p>
								High-frequency repeating schedules (for example, every few minutes) may show a
								simplified indicator on the calendar rather than listing every individual occurrence.
							</p>

							<Subheading>When multiple schedules overlap</Subheading>
							<p>
								If two or more schedules for the same system are due at the same moment, the
								scheduler activates one of them based on an internal priority value (lower numbers
								take precedence). Avoid overlapping schedules when possible — stagger start times by
								a minute or two if two looks must run close together.
							</p>
						</Section>

						<Section id="jules" icon={Sparkles} title="Jules AI">
							<p>
								<strong>Jules</strong> is Simulcast&apos;s AI lighting assistant. Jules understands
								events, holidays, sports, and local occasions — and helps you turn them into scenes
								and schedules without starting from a blank color picker. Jules is available on
								eligible subscription plans; contact your administrator or Phospec Industries if you
								do not see Jules in your account.
							</p>

							<Subheading>Ask Jules (Scene editor)</Subheading>
							<p>
								When editing a scene, switch to the <strong>Jules</strong> tab in the color helper
								panel. Describe a look, event, or mood in plain language — for example, &ldquo;St.
								Patrick&apos;s Day parade&rdquo; or &ldquo;warm welcome for a winter festival.&rdquo;
								Jules invents a scene name, picks colors for each zone, and returns a short message.
								Click <strong>Use name &amp; colors</strong> to apply the result. Nothing is applied
								until you confirm.
							</p>

							<Subheading>Jules suggestions (Schedule editor)</Subheading>
							<p>
								When creating or editing a schedule, Jules can search for events relevant to your
								system&apos;s location and the schedule date. Suggestions are grouped by scope —
								local, provincial/state, national, and global — and include a color preview.
							</p>
							<p>From a suggestion you can:</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Use name</strong> — copy the event name into the schedule.
								</li>
								<li>
									<strong>Create scene &amp; link</strong> — create a new scene with Jules&apos;s
									colors and attach it to the schedule.
								</li>
								<li>
									<strong>Apply colors to linked scene</strong> — update an already-linked scene
									with Jules&apos;s palette.
								</li>
							</ul>
							<p>
								Jules never changes your system silently — every action requires an explicit button
								press.
							</p>

							<Subheading>System settings — Enable Jules</Subheading>
							<p>
								Entitled users can configure Jules per system in the system editor. Open a system
								card for editing and find the <strong>Jules</strong> section:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Enable Jules</strong> — turns on editor suggestions in Scene and Schedule
									editors for this system.
								</li>
								<li>
									<strong>Let Jules decide</strong> — optional autonomous mode. During the daily
									on window, Jules picks an event-themed look and applies it automatically; at the
									off time, Jules blacks out only if it still owns the current look.
								</li>
								<li>
									<strong>Jules on time / off time</strong> — local clock times (in the system
									timezone) for autonomous apply and blackout.
								</li>
								<li>
									<strong>Event class priority</strong> — when several events fall on the same day,
									reorder categories (sports, holidays, local festivals, national events, and more)
									to control which type Jules prefers.
								</li>
							</ul>
							<p>
								Autonomous mode and event discovery require <strong>latitude and longitude</strong>{' '}
								on the system — the same coordinates used for astronomical schedules.
							</p>

							<ExampleBox title="Example: game day without manual prep">
								<p>
									Enable Jules on a sports venue system. Set on time to <strong>17:00</strong> and
									off time to <strong>23:00</strong>. Prioritize <strong>Sports teams</strong> at
									the top of event class priority. On home-game days, Jules applies team colors
									during the evening window automatically.
								</p>
							</ExampleBox>

							<Subheading>Manual control vs. Jules</Subheading>
							<p>
								If you activate a scene manually or a non-Jules schedule fires, Jules releases
								ownership of the current look. Your explicit choices always take precedence over
								autonomous picks.
							</p>
						</Section>

						<Section id="repeating-rules" icon={Clock} title="Repeating Rules">
							<p>
								Repeating schedules use standard recurrence rules (iCalendar RRULE format) to define
								when a schedule runs after its initial start. Enable repeating by choosing{' '}
								<strong>Repeating</strong> in the schedule editor, then click{' '}
								<strong>Edit Repeat Rule</strong> to configure the pattern.
							</p>

							<Subheading>How repeat rules combine with time</Subheading>
							<p>
								A repeat rule does not replace your chosen time — it defines <em>which days</em> (or
								how often) that time applies:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Fixed + repeating</strong> — the date/time fields set the anchor (first
									occurrence and time of day). The repeat rule determines subsequent dates.
								</li>
								<li>
									<strong>Astronomical + repeating</strong> — the start date sets when repetition
									begins. Each occurrence uses the selected sun event and offset, recalculated for
									that calendar day.
								</li>
							</ul>
							<p>
								After saving, the repeat rule summary appears on the schedule card (for example,
								&ldquo;every week on Monday, Wednesday, and Friday&rdquo;).
							</p>

							<Subheading>Repeat every (interval and frequency)</Subheading>
							<p>
								The <strong>Repeat Every</strong> controls set how often the schedule recurs. Enter
								an interval number and choose a unit:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li><strong>Year(s)</strong> — once per year, or every N years</li>
								<li><strong>Month(s)</strong> — monthly on the same day-of-month as the start date</li>
								<li><strong>Week(s)</strong> — weekly, or every N weeks</li>
								<li><strong>Day(s)</strong> — daily, or every N days</li>
								<li><strong>Hour(s)</strong> — every N hours (same minute as the start time)</li>
								<li><strong>Minute(s)</strong> — every N minutes</li>
							</ul>
							<p>
								Examples: interval <strong>1</strong> + <strong>Day(s)</strong> = every day;
								interval <strong>2</strong> + <strong>Week(s)</strong> = every other week;
								interval <strong>15</strong> + <strong>Minute(s)</strong> = every 15 minutes.
							</p>

							<Subheading>Only on these days</Subheading>
							<p>
								When the frequency is weekly (or longer), you can restrict runs to specific weekdays
								using the day checkboxes — Monday through Sunday. Leave all unchecked to allow the
								rule to run on any day allowed by the frequency.
							</p>
							<p>
								Selecting days is most commonly used with a <strong>Week(s)</strong> frequency to
								create weekday-only or weekend-only patterns.
							</p>

							<ExampleBox title="Example: weekdays at 7:00 AM">
								<p>
									Fixed time: <strong>7:00 AM</strong>, Repeat every <strong>1 Week</strong>, days:{' '}
									<strong>Mon, Tue, Wed, Thu, Fri</strong>.
								</p>
								<p>Runs at 7:00 AM every weekday; skipped on Saturday and Sunday.</p>
							</ExampleBox>

							<ExampleBox title="Example: Sunday service">
								<p>
									Fixed time: <strong>9:30 AM</strong>, Repeat every <strong>1 Week</strong>, days:{' '}
									<strong>Sunday</strong>.
								</p>
								<p>Runs every Sunday at 9:30 AM in the system timezone.</p>
							</ExampleBox>

							<Subheading>Duration (when repetition stops)</Subheading>
							<p>
								The <strong>Duration</strong> dropdown controls how long the repeat rule stays active:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Until Stopped</strong> — the schedule repeats indefinitely until you delete
									it or change it to one-time. This is the default.
								</li>
								<li>
									<strong>Until Date</strong> — repetition ends after the chosen end date (inclusive
									of that full calendar day).
								</li>
								<li>
									<strong>Count</strong> — repetition stops after a fixed number of occurrences.
									(Set the count value when this mode is selected.)
								</li>
							</ul>
							<p>
								One-time schedules ignore duration settings — they always run once. The calendar
								will not show occurrences outside the until-date or count limit.
							</p>

							<Subheading>Editing and reviewing repeat rules</Subheading>
							<p>
								Inside the repeat rule panel, a plain-language summary (for example, &ldquo;every 2
								weeks on Tuesday&rdquo;) updates as you change settings. Click <strong>Done</strong>{' '}
								to collapse the editor and return to the schedule form. The summary is also shown on
								the schedule card in the list view.
							</p>
							<p>
								To change an existing repeating schedule, open it from the schedule list or calendar,
								click <strong>Edit Repeat Rule</strong>, adjust the pattern, and save. To convert a
								repeating schedule to a single run, switch <strong>Repeat</strong> to{' '}
								<strong>One-time</strong> — the stored repeat rule is removed on save.
							</p>

							<Subheading>Common repeat patterns</Subheading>
							<div className="space-y-3">
								<ExampleBox title="Every day at a fixed time">
									<p>Repeat every <strong>1 Day</strong>, no day restrictions, Until Stopped.</p>
								</ExampleBox>
								<ExampleBox title="Every hour">
									<p>Repeat every <strong>1 Hour</strong>. Useful for short test cycles or frequent scene rotation.</p>
								</ExampleBox>
								<ExampleBox title="Sunset every day (astronomical)">
									<p>
										Time type: <strong>Astronomical</strong>, Event: <strong>Sunset</strong>,
										Repeat every <strong>1 Day</strong>. Clock time shifts with the seasons.
									</p>
								</ExampleBox>
								<ExampleBox title="Seasonal or limited run">
									<p>
										Set your start date, configure the repeat pattern, then choose{' '}
										<strong>Until Date</strong> for the last day of the season or event series.
									</p>
								</ExampleBox>
							</div>

							<Subheading>Tips for reliable schedules</Subheading>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									Confirm the system <strong>timezone</strong> matches the physical installation
									before relying on fixed-time schedules.
								</li>
								<li>
									For astronomical schedules, verify <strong>latitude</strong> and{' '}
									<strong>longitude</strong> — even small errors can shift sunset times by several
									minutes.
								</li>
								<li>
									Create scenes before schedules; the scene dropdown only lists scenes that already
									exist for the system.
								</li>
								<li>
									Test with a one-time schedule a few minutes ahead before deploying a daily or
									weekly repeat rule.
								</li>
								<li>
									Use <strong>None (blackout)</strong> on a separate repeating schedule for a
									consistent &ldquo;all off&rdquo; time rather than deleting other schedules.
								</li>
							</ul>
						</Section>

						<Section id="gateways" icon={Router} title="Gateways">
							<p>
								Gateways are the hardware devices that connect Simulcast to your fixtures. They are
								what makes <em>lighting control at scale</em> practical: a single system can drive
								any number of gateways, and every gateway outputs the same scene in lockstep when a
								schedule fires. Manage gateways in the <strong>Setup</strong> view.
							</p>
							<Subheading>Building a system of any size</Subheading>
							<p>
								Start with one gateway for a small installation. As you add buildings, universes, or
								geographic sites, register additional gateways under the <strong>same system</strong>{' '}
								— each with its own sACN universe. Simulcast treats them as one cohesive installation:
								one scene activation, one schedule event, all gateways updated together. A downtown
								display might use one gateway per block; a national brand might run parallel systems
								in every city — each system with its own gateways and schedules, all managed from
								one account.
							</p>
							<Subheading>Adding a gateway</Subheading>
							<p>Click <strong>Add Gateway</strong> and configure:</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Name</strong> — a friendly label for the device.
								</li>
								<li>
									<strong>Serial / Device ID</strong> — the unique identifier for the gateway hardware.
								</li>
								<li>
									<strong>sACN Universe</strong> — the DMX universe number the gateway outputs on
									(typically 1–63999).
								</li>
								<li>
									<strong>Location, latitude, longitude</strong> — optional metadata for field
									reference.
								</li>
								<li>
									<strong>Reference / Notes</strong> — optional internal documentation.
								</li>
							</ul>
							<p>
								When you save a gateway, Simulcast links it to the current system and pushes
								configuration to the device. Ensure the gateway is powered, connected to the network,
								and registered with the correct serial number before expecting live output.
							</p>
							<Subheading>Multiple gateways, one system</Subheading>
							<p>
								There is no practical limit to how many gateways you attach to a system. Typical
								layouts include one gateway per building wing, per DMX universe, per structure in a
								citywide installation, or per network segment when fixtures are geographically
								separated. Each gateway can be assigned its own sACN universe while sharing the same
								zones, scenes, and schedules as every other gateway in that system.
							</p>
						</Section>

						<Section id="users" icon={Users} title="Users & Permissions">
							<p>
								The <strong>Users</strong> view lets system owners manage who has access and what they
								can do.
							</p>
							<Subheading>Inviting guests</Subheading>
							<p>
								Click <strong>Invite</strong>, enter the guest&apos;s name and email, and choose
								permissions:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>
									<strong>Control</strong> — activate scenes and blackout.
								</li>
								<li>
									<strong>Write</strong> — edit scenes, schedules, zones, and gateways.
								</li>
								<li>
									<strong>Add</strong> — create new content.
								</li>
								<li>
									<strong>Invite</strong> — invite other users to the system.
								</li>
								<li>
									<strong>Delete</strong> — remove content from the system.
								</li>
							</ul>
							<p>
								Pending invitations appear in the users list until the invitee creates an account and
								accepts access. Remove guests at any time from the same view.
							</p>
						</Section>

						<Section id="live-control" icon={Clock} title="Live Control">
							<p>
								Beyond scheduled automation, you can take manual control at any time from the{' '}
								<strong>Scenes</strong> view:
							</p>
							<ul className="list-disc list-outside ml-5 space-y-1.5">
								<li>Click a scene card to immediately activate that look.</li>
								<li>Click <strong>Blackout</strong> to turn all zones off.</li>
								<li>The current output panel shows active colors per zone.</li>
							</ul>
							<p>
								Manual activations override the previous scene until a schedule fires or you select a
								different scene. Scheduled events resume control according to their configured times
								and priorities.
							</p>
							<p className="text-sm text-gray-500 bg-[#880e4f]/5 rounded-xl px-4 py-3 border border-[#880e4f]/15">
								Need help beyond this guide? Contact Phospec Industries for installation support,
								hardware setup, and advanced configuration.
							</p>
						</Section>
					</main>
				</div>
			</div>

			<div className="bg-gray-950 text-white">
				<Footer />
			</div>
		</div>
	);
};

export default Docs;
